import React from 'react'
import { GithubRepoTile as TileData } from './GithubRepos.interface'
import styles from './GithubRepoTile.module.scss'
import Image from 'next/image'
import { getLanguageColor } from '@/src/utils/getLanguageColor'

import iconComputer from '@/public/assets/images/githubRepos/icon_github_computer.svg'
import iconStar from '@/public/assets/images/githubRepos/icon_github_star.svg'
import iconForks from '@/public/assets/images/githubRepos/icon_github_forks.svg'

interface GithubRepoTileProps {
  data: TileData
}

/**
 * Renders an individual GitHub repository card.
 */
export const GithubRepoTileComponent: React.FC<GithubRepoTileProps> = ({ data }) => {
  const { name, description, language, stargazers_count, forks_count, html_url, visibility } = data

  // Call the utility function to get the color code (e.g., '#f34b7d' or null)
  const languageColor = getLanguageColor(language)
  // Define the inline style object using the returned color
  const languageDotStyle: React.CSSProperties = {
    // Set the background color directly. If null, use a default fallback (e.g., a transparent or gray)
    backgroundColor: languageColor || 'transparent',
    // Set opacity low if the color is null, indicating an undefined language color
    opacity: languageColor === null ? 0.4 : 1,
  }

  return (
    <div
      className={`${styles.gitRepoTile} flex grow flex-col content-start justify-between font-dm-mono w-full!`}
    >
      <div className={`${styles.header} flex flex-row mb-[15px] items-center`}>
        <a href={html_url} target="_blank" rel="noopener noreferrer" className="inline-flex">
          <Image
            src={iconComputer}
            alt="Repo icon"
            width={16}
            height={21}
            className={`inline-block mr-[13px]`}
          />
          <span
            className={`${styles.repoName} ctaLink text-[14px] font-normal tracking-[1.4px] mr-[13px]`}
          >
            {name.toUpperCase()}
          </span>
        </a>
        {/* Visibility Tag */}
        {visibility === 'public' && (
          <span
            className={`inline-flex font-gt-haptik text-[12px] ${styles.publicTag} px-[10px] py-[3px]`}
          >
            Public
          </span>
        )}
      </div>

      {/* Description */}
      <p className={`${styles.description} text-[14px] leading-[150%] font-medium`}>
        {description || 'No description provided.'}
      </p>

      {/* Footer Metrics */}
      <div className={`${styles.footerMetrics} flex flex-row gap-[30px] mt-[15px]`}>
        {/* Language */}
        {language && (
          <div className={styles.metric}>
            <span className={styles.languageDot} style={languageDotStyle} />
            <span className={styles.languageText}>{language}</span>
          </div>
        )}

        {/* Stargazers Count */}
        <div className={styles.metric}>
          <Image
            src={iconStar}
            alt="Stargazers icon"
            width={10}
            height={10}
            className={styles.iconStar}
          />
          <span className={styles.metricCount}>{stargazers_count}</span>
        </div>

        {/* Forks Count */}
        <div className={styles.metric}>
          <Image
            src={iconForks}
            alt="Forks icon"
            width={12}
            height={13}
            className={styles.iconForks}
          />
          <span className={styles.metricCount}>{forks_count}</span>
        </div>
      </div>
    </div>
  )
}
